import { queryYemboApiCall, YemboApiError } from '../queryYemboApiCalls';

import type { MfaChannel, MfaEmployee } from './types';

export type { MfaChannel, MfaEmployee } from './types';

type LoginDevice = {
  accessToken: string;
  key: string;
  userKey: string;
};

export type LoginSuccessResponseWithDevice = { device: LoginDevice };

type LoginSuccessResponse = { employee: MfaEmployee } | LoginSuccessResponseWithDevice;

/**
 * The sign-in routes answer before there is a session, so they never send the
 * stored token: a stale one (an expired session left in this browser) would
 * make the API refuse the sign-in itself with `access_token_expired`.
 */
const signInRequest = { withoutAccessToken: true, logoutIfNotAuthorized: false } as const;

async function handleResponse<T>(response: Response) {
  let jsonResponse: unknown;
  try {
    jsonResponse = await response.json();
  } catch {
    jsonResponse = undefined;
  }
  if (response.ok) return jsonResponse as T;
  throw new YemboApiError(response, jsonResponse);
}

export const putLogin = async (loginData: {
  email: string;
  password: string;
  timezone: string;
  activationToken?: string;
}) => {
  const response = await queryYemboApiCall({
    method: 'put',
    url: 'employee/login',
    ...signInRequest,
    body: JSON.stringify(loginData),
  });

  return handleResponse<LoginSuccessResponse>(response);
};

export const putVerifyMfa = async (
  mfaData: { email: string; password: string } & ({ mfaCode: string } | { backupCode: string })
) => {
  const response = await queryYemboApiCall({
    method: 'put',
    url: 'employee/verify-mfa',
    ...signInRequest,
    body: JSON.stringify(mfaData),
  });

  return handleResponse<LoginSuccessResponseWithDevice>(response);
};

/** `PUT /employee/send-mfa` answers 204. */
export const putSendMfa = async (loginData: {
  email: string;
  password: string;
  channel: Exclude<MfaChannel, 'totp'>;
}) => {
  await queryYemboApiCall({
    method: 'put',
    url: 'employee/send-mfa',
    ...signInRequest,
    body: JSON.stringify(loginData),
    shouldNotifyAndThrowError: true,
  });
};

/** `PUT /employee/request-password-reset` answers 204 whether or not the address has an account. */
export const putRequestPasswordReset = async (email: string) => {
  await queryYemboApiCall({
    method: 'put',
    url: 'employee/request-password-reset',
    ...signInRequest,
    shouldNotifyAndThrowError: true,
    body: JSON.stringify({ email }),
  });
};

/**
 * `PUT /employee/reset-password` answers 204, and signs the employee out of
 * every session they had, this browser's included.
 */
export const putResetPassword = async (resetData: { email: string; password: string; passwordResetToken: string }) => {
  await queryYemboApiCall({
    method: 'put',
    url: 'employee/reset-password',
    ...signInRequest,
    shouldNotifyAndThrowError: true,
    body: JSON.stringify(resetData),
  });
};

/**
 * `PUT /employee/logout` ends this browser's session on the server (204). It
 * sends the token — that is what it revokes — and never signs out on a 401: the
 * caller is signing out anyway.
 */
export const putLogout = async () => {
  await queryYemboApiCall({
    method: 'put',
    url: 'employee/logout',
    logoutIfNotAuthorized: false,
    shouldThrowError: true,
  });
};
